import { useMemo, useEffect, useState, RefObject } from "react";
import {
    ViewListingReviewsSubtitle,
    ViewListingReviewsReviewWrapper,
    ViewListingReviewsReviewText,
    ViewListingReviewsReviewDate,
    ViewListingReviewsReviewImage,
    ViewListingReviewsReviewTextAvatar,
    ViewListingHeaderProfileImageVerified,
    ViewListingReviewsReviewName,
    ViewListingReviewsWrapper,
    ViewListingReviewsItemWrapper,
    ViewListingReviewsHeaderWrapper,
    ViewListingReviewsHeaderRating,
    ViewListingReviewsHeader,
    ViewListingReviewsHeaderRatingTitle,
    ViewListingReviewsHeaderRatingOutOf,
    ViewListingReviewsCommentSwiper,
    ViewListingReviewsCommentSwiperWrapper,
    ViewListingReviewsSeeAllReviewBtn,
    ViewListingNoReviewsTitle,
    ViewListingNoReviewsCommentWrapper,
    ViewListingNoReviewsCommentTitle,
    ViewListingNoReviewsCommentSubtitle,
    ReadMoreButton,
    ViewListingReviewsExternalPopup,
    ViewListingReviewsHeaderRatingStar,
} from "../viewListingStyling";
import { ListingInterface } from "../viewListing";
import { ReviewStayModel } from "../../../interfaces/models/reviewStayModel";
import constructOptimisedProtectedImageUrl from "../../../utils/imageOptimisation/constructOptimisedProtectedImageUrl";
import useClickOutside from "../../../hook/useClickOutside";
import RatingDistribution from "./ratingDistribution";
import { ColorTheme } from "../../../components/globaStyledComponents/globalStyledComponentsStyling";



export default function ViewListingReviews({
    listing,
    reviews,
    toggleSeeReview,
    reviewsRef,
}: {
    listing: ListingInterface;
    reviews: ReviewStayModel[];
    toggleSeeReview: () => Promise<void>;
    reviewsRef: RefObject<HTMLDivElement>;
}) {
    const renderReview = useMemo(() => {
        let reviewsData = [];
        for (let i = 0; i < reviews.length; i++) {
            if (reviews[i].body && !reviews[i].hidden) {
                reviewsData.push(reviews[i]);
            }
        }
        return reviewsData;
    }, [reviews]);

    const ratingCounts = useMemo(() => {
        const counts: { [key: number]: number } = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        for (const review of reviews) {
            if (review.overall && !review.hidden) {
                const r = Math.round(review.overall / 10);
                if (counts[r] !== undefined) counts[r]++;
            }
        }
        return counts;
    }, [reviews]);

    return (
        <ViewListingReviewsWrapper ref={reviewsRef}>
            <ViewListingReviewsHeaderWrapper>
                {listing.overallAverage > 0 ? (
                    <>
                        <ViewListingReviewsHeaderRating>
                            <ViewListingReviewsHeaderRatingTitle>
                                <ViewListingReviewsHeaderRatingStar>
                                    <svg width="18" height="18" viewBox="0 0 31 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M14.2997 0.885909C14.7438 -0.181772 16.2562 -0.181774 16.7003 0.885907L19.8239 8.39584C20.0111 8.84595 20.4344 9.15349 20.9203 9.19244L29.0279 9.84243C30.1806 9.93484 30.6479 11.3733 29.7697 12.1256L23.5926 17.4169C23.2224 17.7341 23.0607 18.2317 23.1738 18.7059L25.061 26.6175C25.3293 27.7423 24.1057 28.6313 23.1189 28.0286L16.1776 23.7889C15.7616 23.5348 15.2384 23.5348 14.8224 23.7889L7.88113 28.0286C6.8943 28.6313 5.67067 27.7423 5.93898 26.6175L7.82619 18.7059C7.9393 18.2317 7.77762 17.7341 7.40739 17.4169L1.23025 12.1256C0.352056 11.3733 0.819438 9.93484 1.97209 9.84243L10.0797 9.19245C10.5656 9.15349 10.9889 8.84595 11.1761 8.39584L14.2997 0.885909Z"
                                            fill={ColorTheme.Black.color}
                                        />
                                    </svg>
                                </ViewListingReviewsHeaderRatingStar>
                                {(listing.overallAverage / 10).toFixed(2)}
                            </ViewListingReviewsHeaderRatingTitle>
                            <ViewListingReviewsHeaderRatingOutOf>out of 5</ViewListingReviewsHeaderRatingOutOf>
                        </ViewListingReviewsHeaderRating>

                        <ViewListingReviewsHeader>
                            <RatingDistribution ratingCounts={ratingCounts} />
                        </ViewListingReviewsHeader>
                    </>
                ) : (
                    <ViewListingReviewsHeader>
                        <ViewListingNoReviewsTitle>No reviews yet</ViewListingNoReviewsTitle>
                        <ViewListingReviewsSubtitle>This place has not been reviewed yet.</ViewListingReviewsSubtitle>
                    </ViewListingReviewsHeader>
                )}
            </ViewListingReviewsHeaderWrapper>

            {renderReview && reviews.length > 0 ? (
                <ViewListingReviewsCommentSwiperWrapper isShow={renderReview.length > 0}>
                    <ViewListingReviewsCommentSwiper>
                        <ViewListingReviewsItemWrapper>
                            {renderReview.map((review, index) => {
                                return <Review review={review} key={index} />;
                            })}
                        </ViewListingReviewsItemWrapper>
                    </ViewListingReviewsCommentSwiper>
                </ViewListingReviewsCommentSwiperWrapper>
            ) : (
                <ViewListingNoReviewsCommentWrapper>
                    <ViewListingNoReviewsCommentTitle>Be the first review here</ViewListingNoReviewsCommentTitle>
                    <ViewListingNoReviewsCommentSubtitle>
                        Reviews written by past guests<br />
                        will appear here.
                    </ViewListingNoReviewsCommentSubtitle>
                </ViewListingNoReviewsCommentWrapper>
            )}

            {renderReview && renderReview.length > 6 && <ViewListingReviewsSeeAllReviewBtn onClick={toggleSeeReview}>Show all {renderReview.length} reviews</ViewListingReviewsSeeAllReviewBtn>}
        </ViewListingReviewsWrapper>
    );
}

function Review({ review }: { review: ReviewStayModel }) {
    const [reviewOwnerProfilePictureURL, setReviewOwnerProfilePictureURL] = useState<string | null>(null);

    const [isExpanded, setIsExpanded] = useState(true);

    const [externalSource, setExternalSource] = useState(false);

    const handleClick = () => {
        setExternalSource(!externalSource);
    };

    const wrapperRef = useClickOutside(externalSource, handleClick, "click");

    useEffect(() => {
        if (review?.UserModel?.profilePicture) {
            const reviewOwnerProfilePic = constructOptimisedProtectedImageUrl(review.UserModel.profilePicture, review.UserModel.identityID, 100);
            setReviewOwnerProfilePictureURL(reviewOwnerProfilePic);
        }
    }, [review]);

    if (!review.body) return null;

    return (
        <ViewListingReviewsReviewWrapper>
            <ViewListingReviewsReviewText>
                {isExpanded ? (review.body.length > 180 ? review.body.slice(0, 180) + "..." : review.body) : review.body}{" "}
                {review.body.length > 180 && <ReadMoreButton onClick={() => setIsExpanded(!isExpanded)}>{isExpanded ? "Read more" : "Show less"}</ReadMoreButton>}
            </ViewListingReviewsReviewText>
            <div style={{ display: "flex", gap:"16px" }}>
                {reviewOwnerProfilePictureURL ? (
                    <div style={{ position: "relative", display: "inline-block" }}>
                        <ViewListingReviewsReviewImage src={reviewOwnerProfilePictureURL} alt="Picture of the guest who made the review" onClick={handleClick} />
                        <ViewListingHeaderProfileImageVerified src="https://d292awxalydr86.cloudfront.net/Universal+icons/verified+profile.svg" />
                    </div>
                ) : (
                    <div>
                        <ViewListingReviewsReviewTextAvatar onClick={handleClick}>
                            {review?.UserModel?.firstName?.charAt(0) + review?.UserModel?.lastName?.charAt(0)}
                            <ViewListingHeaderProfileImageVerified src="https://d292awxalydr86.cloudfront.net/Universal+icons/verified+profile.svg" />
                        </ViewListingReviewsReviewTextAvatar>
                    </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", maxHeight: "100%" }}>
                    <ViewListingReviewsReviewName>{review?.UserModel?.firstName ? review.UserModel.firstName : review.externalUserFirstName}</ViewListingReviewsReviewName>
                    <ViewListingReviewsReviewDate>{new Date(review.createdAt).toLocaleDateString("en-AU", { month: "short", year: "numeric" })}</ViewListingReviewsReviewDate>
                </div>
            </div>
            {externalSource && !review.usermodelID && (
                <ViewListingReviewsExternalPopup ref={wrapperRef}>
                    This review was made from a trusted external source. <strong style={{ textDecoration: "underline" }}>Learn more</strong>
                </ViewListingReviewsExternalPopup>
            )}
        </ViewListingReviewsReviewWrapper>
    );
}
